import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import {
  MdAdd,
  MdAssignment,
  MdBuild,
  MdCategory,
  MdDelete,
  MdEdit,
  MdExpandMore,
  MdInventory,
  MdMoreVert,
  MdRefresh,
  MdSearch,
  MdSpeed,
  MdVisibility,
} from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { useToast } from '../../components/shared/Toast';
import { Pagination } from '../../components/shared/Pagination';
import { cn } from '../../lib/utils';
import { ClearFiltersButton, PageSkeleton } from '@euroasiann/shared/ui';

type TemplateTask = {
  taskKey: string;
  taskName: string;
  taskType?: string;
  frequencyType: string;
  runningHourInterval?: number;
  criticality: string;
  responsibleRank?: string;
  description?: string;
};

type EquipmentTypesCatalog = {
  groups: Array<{
    id: string;
    label: string;
    types: Array<{ id: string; label: string; shortLabel: string; rhBased: boolean; description: string }>;
  }>;
  types: Array<{ id: string; label: string; shortLabel: string }>;
  rhIntervals: number[];
  responsibleRanks: string[];
  taskTypes: Array<{ id: string; label: string }>;
  frequencyTypes: Array<{ id: string; label: string }>;
};

type MaintenanceTemplate = {
  _id: string;
  templateKey: string;
  maker: string;
  model: string;
  equipmentType: string;
  label: string;
  description?: string;
  status: string;
  tasks: TemplateTask[];
};

function EquipmentTypeSelect({
  value,
  onChange,
  catalog,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  catalog: EquipmentTypesCatalog | null;
  className?: string;
}) {
  if (!catalog) {
    return (
      <select className={className} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="auxiliary-engine">Auxiliary Engine</option>
      </select>
    );
  }
  return (
    <select className={className} value={value} onChange={(e) => onChange(e.target.value)}>
      {catalog.groups.map((g) => (
        <optgroup key={g.id} label={g.label}>
          {g.types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

const CRITICALITY = ['low', 'medium', 'high', 'critical'] as const;

const inputClass =
  'mt-1 w-full px-3.5 py-2.5 border border-[hsl(var(--border))] rounded-xl text-sm font-normal bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] shadow-sm focus:ring-2 focus:ring-[#0088cc]/25 focus:border-[#0088cc]/50 outline-none transition-shadow';

  const selectClass =
  'w-full h-11 pl-3.5 pr-10 border border-[hsl(var(--border))] rounded-xl text-sm bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] shadow-sm focus:ring-2 focus:ring-[#0088cc]/25 focus:border-[#0088cc]/50 outline-none transition-shadow appearance-none cursor-pointer';

const labelClass =
  'block text-[11px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-[0.08em]';

const emptyPackForm = {
  equipmentType: 'auxiliary-engine',
  maker: '',
  model: '',
  label: '',
  description: '',
};

const emptyTaskForm = {
  taskName: '',
  taskType: 'routine_maintenance',
  frequencyType: 'running_hours',
  runningHourInterval: '600',
  criticality: 'high',
  responsibleRank: '2/E',
  description: '',
};

const DEFAULT_LIMIT = 10;

function PackRowActions({
  pack,
  onView,
  onEdit,
  onDelete,
}: {
  pack: MaintenanceTemplate;
  onView: (pack: MaintenanceTemplate) => void;
  onEdit: (pack: MaintenanceTemplate) => void;
  onDelete: (pack: MaintenanceTemplate) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = 168;
    let left = rect.right - width;
    if (left < 8) left = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 140;
    setMenuStyle({
      position: 'fixed',
      zIndex: 2300,
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      left,
      width,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onMove = () => updateMenuPosition();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Pack actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-lg text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
      >
        <MdMoreVert className="w-5 h-5" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={menuStyle}
              className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-1 shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                onClick={() => {
                  setOpen(false);
                  onView(pack);
                }}
              >
                <MdVisibility className="w-4 h-4 text-[#0088cc]" />
                View
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                onClick={() => {
                  setOpen(false);
                  onEdit(pack);
                }}
              >
                <MdEdit className="w-4 h-4" />
                Edit
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-500/10"
                onClick={() => {
                  setOpen(false);
                  onDelete(pack);
                }}
              >
                <MdDelete className="w-4 h-4" />
                Delete
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export function AdminOemPmsPage() {
  const { showToast } = useToast();
  const [templates, setTemplates] = useState<MaintenanceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [panel, setPanel] = useState<{ key: string; mode: 'view' | 'edit' } | null>(null);
  const [activeDetail, setActiveDetail] = useState<MaintenanceTemplate | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingRh, setEditingRh] = useState<Record<string, string>>({});
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterMaker, setFilterMaker] = useState('');
  const [filterTaskCount, setFilterTaskCount] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreatePack, setShowCreatePack] = useState(false);
  const [packForm, setPackForm] = useState(emptyPackForm);
  const [taskForms, setTaskForms] = useState<Record<string, typeof emptyTaskForm>>({});
  const [editMeta, setEditMeta] = useState<Record<string, { maker: string; model: string; equipmentType: string }>>({});
  const [equipmentCatalog, setEquipmentCatalog] = useState<EquipmentTypesCatalog | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    authenticatedFetch('/api/v1/admin/pms/equipment-types', { signal: ac.signal })
      .then((r) => r.json())
      .then((json) => {
        if (!ac.signal.aborted && json.success) setEquipmentCatalog(json.data);
      })
      .catch(() => {});
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const next = searchQuery.trim();
    if (next === debouncedSearch) return;
    const timer = window.setTimeout(() => setDebouncedSearch(next), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    setPage((prev) => (prev === 1 ? prev : 1));
  }, [filterType, filterMaker, debouncedSearch, limit]);

  const typeLabel = useCallback(
    (id: string) => equipmentCatalog?.types.find((t) => t.id === id)?.label ?? id,
    [equipmentCatalog]
  );

  const loadTemplates = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const sp = new URLSearchParams({
          status: 'published',
          page: String(page),
          limit: String(limit),
        });
        if (filterType) sp.set('equipmentType', filterType);
        if (filterMaker) sp.set('maker', filterMaker);
        if (debouncedSearch) sp.set('search', debouncedSearch);
        const res = await authenticatedFetch(`/api/v1/admin/pms/templates?${sp}`, { signal });
        if (signal?.aborted) return;
        const json = await res.json();
        if (signal?.aborted) return;
        if (!res.ok) throw new Error(json.error || 'Failed to load templates');
        const rows = (Array.isArray(json.data) ? json.data : []) as MaintenanceTemplate[];
        const pagination = json.pagination || {};
        setTemplates(rows);
        setTotal(Number(pagination.total ?? json.total ?? rows.length) || 0);
        setTotalPages(
          Number(
            pagination.totalPages ??
              json.totalPages ??
              Math.max(1, Math.ceil((Number(pagination.total ?? json.total ?? rows.length) || 0) / limit))
          ) || 1
        );
      } catch (e: any) {
        if (signal?.aborted || e?.name === 'AbortError') return;
        showToast(e instanceof Error ? e.message : 'Load failed', 'error');
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [showToast, filterType, filterMaker, debouncedSearch, page, limit]
  );

  useEffect(() => {
    const ac = new AbortController();
    void loadTemplates(ac.signal);
    return () => ac.abort();
  }, [loadTemplates]);

  const libraryStats = useMemo(
    () => ({
      packs: total,
      tasks: templates.reduce((sum, t) => sum + (t.tasks?.length || 0), 0),
      types: new Set(templates.map((t) => t.equipmentType)).size,
    }),
    [templates, total]
  );

  const makerOptions = useMemo(() => {
    const makers = new Set(templates.map((t) => t.maker).filter(Boolean));
    if (filterMaker) makers.add(filterMaker);
    return Array.from(makers).sort((a, b) => a.localeCompare(b));
  }, [templates, filterMaker]);

  const displayedTemplates = useMemo(() => {
    if (!filterTaskCount) return templates;
    return templates.filter((t) => {
      const count = t.tasks?.length || 0;
      switch (filterTaskCount) {
        case '0':
          return count === 0;
        case '1-5':
          return count >= 1 && count <= 5;
        case '6-10':
          return count >= 6 && count <= 10;
        case '11+':
          return count >= 11;
        default:
          return true;
      }
    });
  }, [templates, filterTaskCount]);

  const seedLibrary = async () => {
    setSeeding(true);
    try {
      const res = await authenticatedFetch('/api/v1/admin/pms/templates/seed-oem', { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Seed failed');
      showToast(`OEM library seeded: ${json.data.created} new, ${json.data.updated} updated`, 'success');
      loadTemplates();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Seed failed', 'error');
    } finally {
      setSeeding(false);
    }
  };

  const createPack = async () => {
    if (!packForm.maker.trim() || !packForm.model.trim()) {
      showToast('Maker and model are required', 'warning');
      return;
    }
    try {
      const res = await authenticatedFetch('/api/v1/admin/pms/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          maker: packForm.maker.trim(),
          model: packForm.model.trim(),
          equipmentType: packForm.equipmentType,
          label: packForm.label.trim() || undefined,
          description: packForm.description.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Create failed');
      showToast('Equipment PMS pack created — add tasks below', 'success');
      setPackForm(emptyPackForm);
      setShowCreatePack(false);
      if (json.data?.templateKey) {
        await openPanel(json.data.templateKey, 'edit');
      }
      loadTemplates();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Create failed', 'error');
    }
  };

  const saveTemplateMeta = async (templateKey: string) => {
    const meta = editMeta[templateKey];
    if (!meta) return;
    try {
      const res = await authenticatedFetch(`/api/v1/admin/pms/templates/${templateKey}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          maker: meta.maker,
          model: meta.model,
          equipmentType: meta.equipmentType,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Update failed');
      showToast('Equipment pack updated', 'success');
      setActiveDetail(json.data || null);
      loadTemplates();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Update failed', 'error');
    }
  };

  const deletePack = async (templateKey: string, label: string) => {
    if (!confirm(`Delete equipment pack "${label}"? This cannot be undone from the library list.`)) return;
    try {
      const res = await authenticatedFetch(`/api/v1/admin/pms/templates/${templateKey}`, {
        method: 'DELETE',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      showToast('Equipment pack deleted', 'success');
      if (panel?.key === templateKey) {
        setPanel(null);
        setActiveDetail(null);
      }
      loadTemplates();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Delete failed', 'error');
    }
  };

  const addTask = async (templateKey: string) => {
    const form = taskForms[templateKey] || emptyTaskForm;
    if (!form.taskName.trim()) {
      showToast('Task name is required', 'warning');
      return;
    }
    try {
      const body: Record<string, unknown> = {
        taskName: form.taskName.trim(),
        taskType: form.taskType,
        frequencyType: form.frequencyType,
        criticality: form.criticality,
        responsibleRank: form.responsibleRank.trim() || undefined,
        description: form.description.trim() || undefined,
      };
      if (form.frequencyType === 'running_hours') {
        body.runningHourInterval = Number(form.runningHourInterval);
      }
      const res = await authenticatedFetch(`/api/v1/admin/pms/templates/${templateKey}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Add task failed');
      showToast('Task added to pack', 'success');
      setTaskForms((prev) => ({ ...prev, [templateKey]: emptyTaskForm }));
      if (json.data) setActiveDetail(json.data);
      loadTemplates();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Add task failed', 'error');
    }
  };

  const deleteTask = async (templateKey: string, taskKey: string) => {
    if (!confirm('Remove this task from the equipment pack?')) return;
    try {
      const res = await authenticatedFetch(`/api/v1/admin/pms/templates/${templateKey}/tasks/${taskKey}`, {
        method: 'DELETE',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      showToast('Task removed', 'success');
      if (json.data) setActiveDetail(json.data);
      loadTemplates();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Delete failed', 'error');
    }
  };

  const saveTaskRh = async (templateKey: string, taskKey: string) => {
    const key = `${templateKey}:${taskKey}`;
    const val = Number(editingRh[key]);
    if (!val || val < 1) {
      showToast('Enter a valid RH interval', 'warning');
      return;
    }
    try {
      const res = await authenticatedFetch(`/api/v1/admin/pms/templates/${templateKey}/tasks/${taskKey}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ runningHourInterval: val, frequencyType: 'running_hours' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Update failed');
      showToast('Task interval updated', 'success');
      if (json.data) setActiveDetail(json.data);
      loadTemplates();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Update failed', 'error');
    }
  };

  const getTaskForm = (templateKey: string) => taskForms[templateKey] || emptyTaskForm;

  const openPanel = async (templateKey: string, mode: 'view' | 'edit') => {
    if (panel?.key === templateKey && panel.mode === mode) {
      setPanel(null);
      setActiveDetail(null);
      return;
    }
    setPanel({ key: templateKey, mode });
    setDetailLoading(true);
    try {
      const res = await authenticatedFetch(`/api/v1/admin/pms/templates/${encodeURIComponent(templateKey)}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load pack');
      const detail = json.data as MaintenanceTemplate;
      setActiveDetail(detail);
      setEditMeta((prev) => ({
        ...prev,
        [templateKey]: {
          maker: detail.maker,
          model: detail.model,
          equipmentType: detail.equipmentType,
        },
      }));
      if (!taskForms[templateKey]) {
        setTaskForms((prev) => ({ ...prev, [templateKey]: emptyTaskForm }));
      }
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Failed to load pack', 'error');
      setPanel(null);
      setActiveDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Page header */}
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6 xl:gap-10">
        <div className="space-y-2.5 min-w-0 flex-1">
          <h1 className="text-[2rem] sm:text-[2.25rem] lg:text-[2.35rem] font-extrabold tracking-tight text-[hsl(var(--foreground))] flex items-center gap-4">
            <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0088cc]/25 to-[#0088cc]/5 border border-[#0088cc]/30 shadow-[0_10px_28px_rgba(0,136,204,0.18)] flex items-center justify-center shrink-0">
              <MdBuild className="text-[#0088cc] w-7 h-7" />
            </span>
            <span className="leading-[1.15]">OEM PMS Library</span>
          </h1>
          <p className="text-[0.9375rem] sm:text-base text-[hsl(var(--muted-foreground))] max-w-3xl leading-relaxed font-medium pl-0 sm:pl-[4.5rem]">
            Define equipment type, maker, and model — then add RH or calendar tasks manually. Customer vessels apply
            these packs per machine.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0 w-full sm:w-auto xl:pt-1">
          <button
            type="button"
            onClick={() => setShowCreatePack((v) => !v)}
            className="inline-flex items-center justify-center gap-2 h-11 px-6 bg-[hsl(var(--foreground))] text-[hsl(var(--background))] rounded-xl text-sm font-semibold hover:opacity-90 transition-all duration-200 shadow-[0_4px_14px_rgba(0,0,0,0.18)] w-full sm:w-auto"
          >
            <MdAdd className="w-4 h-4" />
            New equipment pack
          </button>
          <button
            type="button"
            onClick={seedLibrary}
            disabled={seeding}
            className="inline-flex items-center justify-center gap-2 h-11 px-6 bg-[#0088cc] text-white rounded-xl text-sm font-semibold hover:bg-[#0077b3] transition-all duration-200 shadow-[0_4px_16px_rgba(0,136,204,0.35)] disabled:opacity-60 w-full sm:w-auto"
          >
            <MdRefresh className={cn('w-4 h-4', seeding && 'animate-spin')} />
            {seeding ? 'Seeding…' : 'Seed built-in library'}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      {!loading && templates.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 xl:gap-5">
          {[
            { label: 'Equipment Packs', value: libraryStats.packs, icon: MdInventory, tint: 'text-[#0088cc]', iconBg: 'from-[#0088cc]/20 to-[#0088cc]/5 border-[#0088cc]/25' },
            { label: 'Total Tasks', value: libraryStats.tasks, icon: MdAssignment, tint: 'text-emerald-500 dark:text-emerald-400', iconBg: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/25' },
            { label: 'Equipment Types', value: libraryStats.types, icon: MdCategory, tint: 'text-sky-500 dark:text-sky-400', iconBg: 'from-sky-500/20 to-sky-500/5 border-sky-500/25' },
          ].map((kpi) => (
            <div
              key={kpi.label}
              className="group relative overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/75 backdrop-blur-md px-6 py-6 xl:px-7 xl:py-7 shadow-[0_8px_28px_rgba(0,0,0,0.14)] hover:shadow-[0_16px_40px_rgba(0,136,204,0.16)] hover:border-[#0088cc]/35 hover:-translate-y-0.5 transition-all duration-300 ease-out"
            >
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.05] via-transparent to-transparent" />
              <div className="relative flex items-center gap-5">
                <div
                  className={cn(
                    'w-14 h-14 rounded-2xl bg-gradient-to-br border flex items-center justify-center shadow-inner shrink-0',
                    kpi.iconBg,
                    kpi.tint
                  )}
                >
                  <kpi.icon className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <div className={cn('text-[1.75rem] xl:text-[2rem] font-bold tabular-nums tracking-tight leading-none', kpi.tint)}>
                    {kpi.value.toLocaleString()}
                  </div>
                  <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))]">
                    {kpi.label}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreatePack && (
        <div className="bg-[hsl(var(--card))]/75 backdrop-blur-md border border-[hsl(var(--border))] rounded-2xl p-5 sm:p-6 xl:p-7 shadow-[0_8px_28px_rgba(0,0,0,0.12)] space-y-5">
          <h2 className="text-xs font-semibold text-[hsl(var(--foreground))] uppercase tracking-[0.1em]">Create equipment PMS pack</h2>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <label className={labelClass}>
              Equipment type
              <EquipmentTypeSelect
                className={inputClass}
                value={packForm.equipmentType}
                onChange={(v) => setPackForm((f) => ({ ...f, equipmentType: v }))}
                catalog={equipmentCatalog}
              />
            </label>
            <label className={labelClass}>
              Maker *
              <input
                className={inputClass}
                placeholder="e.g. Hyundai Himsen"
                value={packForm.maker}
                onChange={(e) => setPackForm((f) => ({ ...f, maker: e.target.value }))}
              />
            </label>
            <label className={labelClass}>
              Model *
              <input
                className={inputClass}
                placeholder="e.g. H24/30"
                value={packForm.model}
                onChange={(e) => setPackForm((f) => ({ ...f, model: e.target.value }))}
              />
            </label>
            <label className={cn(labelClass, 'sm:col-span-2 xl:col-span-2')}>
              Label (optional)
              <input
                className={inputClass}
                placeholder="Auto-generated from maker + model"
                value={packForm.label}
                onChange={(e) => setPackForm((f) => ({ ...f, label: e.target.value }))}
              />
            </label>
            <label className={cn(labelClass, 'sm:col-span-2 xl:col-span-4')}>
              Description
              <input
                className={inputClass}
                placeholder="Short note for customer engineers"
                value={packForm.description}
                onChange={(e) => setPackForm((f) => ({ ...f, description: e.target.value }))}
              />
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
            <button type="button" onClick={createPack} className="inline-flex items-center justify-center h-10 px-5 bg-[#0088cc] text-white rounded-xl text-sm font-semibold hover:bg-[#0077b3] transition-colors shadow-[0_4px_14px_rgba(0,136,204,0.3)]">
              Create pack
            </button>
            <button type="button" onClick={() => setShowCreatePack(false)} className="inline-flex items-center justify-center h-10 px-5 text-[hsl(var(--muted-foreground))] text-sm font-semibold hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/40 rounded-xl transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search & filters */}
      <div className="bg-[hsl(var(--card))]/75 backdrop-blur-md border border-[hsl(var(--border))] rounded-2xl p-5 sm:p-6 xl:p-7 shadow-[0_8px_28px_rgba(0,0,0,0.12)] space-y-5">
        <div className="flex flex-col xl:flex-row xl:items-end gap-4 xl:gap-5">
          <div className="relative flex-1 min-w-0">
            <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--muted-foreground))]/70 pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search OEM Packs..."
              className="w-full h-12 xl:h-[3.25rem] pl-12 pr-4 border border-[hsl(var(--border))] rounded-2xl text-[0.9375rem] bg-[hsl(var(--background))]/70 text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/55 shadow-sm focus:ring-2 focus:ring-[#0088cc]/25 focus:border-[#0088cc]/50 outline-none transition-all duration-200"
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 w-full xl:w-auto xl:min-w-[38rem] xl:max-w-3xl xl:shrink-0">
            <label className="space-y-2 block min-w-0">
              <span className={labelClass}>Equipment Type</span>
              <div className="relative">
                <select className={selectClass} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="">All equipment types</option>
                  {equipmentCatalog?.groups.map((g) => (
                    <optgroup key={g.id} label={g.label}>
                      {g.types.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <MdExpandMore className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              </div>
            </label>
            <label className="space-y-2 block min-w-0">
              <span className={labelClass}>Maker</span>
              <div className="relative">
                <select className={selectClass} value={filterMaker} onChange={(e) => setFilterMaker(e.target.value)}>
                  <option value="">All makers</option>
                  {makerOptions.map((maker) => (
                    <option key={maker} value={maker}>
                      {maker}
                    </option>
                  ))}
                </select>
                <MdExpandMore className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              </div>
            </label>
            <label className="space-y-2 block min-w-0">
              <span className={labelClass}>Task Count</span>
              <div className="relative">
                <select className={selectClass} value={filterTaskCount} onChange={(e) => setFilterTaskCount(e.target.value)}>
                  <option value="">Any count</option>
                  <option value="0">No tasks</option>
                  <option value="1-5">1 – 5 tasks</option>
                  <option value="6-10">6 – 10 tasks</option>
                  <option value="11+">11+ tasks</option>
                </select>
                <MdExpandMore className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              </div>
            </label>
          </div>
          <ClearFiltersButton
            visible={!!searchQuery.trim() || !!filterType || !!filterMaker || !!filterTaskCount}
            onClick={() => {
              setSearchQuery('');
              setFilterType('');
              setFilterMaker('');
              setFilterTaskCount('');
            }}
            variant="toolbar"
          />
        </div>
        <div className="text-xs sm:text-[13px] text-[hsl(var(--muted-foreground))] font-medium pt-1 border-t border-[hsl(var(--border))]/70">
          Showing <strong className="text-[hsl(var(--foreground))] font-semibold">{displayedTemplates.length}</strong> of{' '}
          <strong className="text-[hsl(var(--foreground))] font-semibold">{total}</strong> packs · page{' '}
          <strong className="text-[hsl(var(--foreground))] font-semibold">{page}</strong> ·{' '}
          <strong className="text-[hsl(var(--foreground))] font-semibold">{libraryStats.tasks}</strong> tasks on this page
        </div>
      </div>

      {loading ? (
        <PageSkeleton variant="table" />
      ) : templates.length === 0 ? (
        <div className="py-20 text-center bg-[hsl(var(--card))]/70 backdrop-blur-md border border-[hsl(var(--border))]/80 rounded-2xl shadow-[0_8px_28px_rgba(0,0,0,0.1)]">
          <MdInventory className="w-12 h-12 text-[hsl(var(--muted-foreground))] mx-auto mb-3 opacity-50" />
          <p className="text-[hsl(var(--muted-foreground))] mb-4">No templates yet.</p>
          <button type="button" onClick={() => setShowCreatePack(true)} className="text-[#0088cc] font-semibold text-sm hover:underline">
            Create your first equipment pack
          </button>
        </div>
      ) : displayedTemplates.length === 0 ? (
        <div className="py-16 text-center bg-[hsl(var(--card))]/70 backdrop-blur-md border border-dashed border-[hsl(var(--border))] rounded-2xl">
          <MdSearch className="w-10 h-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3 opacity-50" />
          <p className="text-sm font-semibold text-[hsl(var(--foreground))]">No packs match your search or filters.</p>
          <ClearFiltersButton
            visible
            onClick={() => {
              setSearchQuery('');
              setFilterType('');
              setFilterMaker('');
              setFilterTaskCount('');
            }}
            variant="empty"
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="overflow-x-auto rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/80 shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
                  <th className="py-3 px-4 font-semibold">Pack</th>
                  <th className="py-3 px-3 font-semibold">Maker</th>
                  <th className="py-3 px-3 font-semibold">Model</th>
                  <th className="py-3 px-3 font-semibold">Category</th>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Tasks</th>
                  <th className="py-3 px-3 font-semibold text-right whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {displayedTemplates.map((t) => {
                  const isActive = panel?.key === t.templateKey;
                  return (
                    <tr
                      key={t.templateKey}
                      className={cn(
                        'hover:bg-[hsl(var(--muted))]/30 transition-colors',
                        isActive && 'bg-[#0088cc]/5'
                      )}
                    >
                      <td className="py-3 px-4">
                        <div className="font-semibold text-[hsl(var(--foreground))]">{t.label}</div>
                        <div className="text-[11px] text-[hsl(var(--muted-foreground))] font-mono mt-0.5">{t.templateKey}</div>
                      </td>
                      <td className="py-3 px-3 text-[hsl(var(--foreground))]">{t.maker}</td>
                      <td className="py-3 px-3 text-[hsl(var(--muted-foreground))] font-mono text-xs">{t.model}</td>
                      <td className="py-3 px-3 text-[hsl(var(--muted-foreground))]">{typeLabel(t.equipmentType)}</td>
                      <td className="py-3 px-3 font-semibold tabular-nums text-[#0088cc]">{t.tasks?.length || 0}</td>
                      <td className="py-3 px-3">
                        <PackRowActions
                          pack={t}
                          onView={(pack) => void openPanel(pack.templateKey, 'view')}
                          onEdit={(pack) => void openPanel(pack.templateKey, 'edit')}
                          onDelete={(pack) => void deletePack(pack.templateKey, pack.label)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <Pagination
              currentPage={page}
              totalPages={Math.max(1, totalPages)}
              onPageChange={setPage}
              itemsPerPage={limit}
              totalItems={total}
              alwaysShow
              onItemsPerPageChange={(nextLimit) => {
                setLimit(nextLimit);
                setPage(1);
              }}
            />
          )}

          {panel && (
            <div className="rounded-2xl border border-[#0088cc]/35 bg-[hsl(var(--card))]/90 shadow-[0_12px_32px_rgba(0,136,204,0.12)] overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[hsl(var(--border))] bg-[#0088cc]/8">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#0088cc]">
                    {panel.mode === 'edit' ? 'Edit pack' : 'View pack'}
                  </p>
                  <h3 className="font-bold text-[hsl(var(--foreground))] truncate">
                    {activeDetail?.label || panel.key}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPanel(null);
                    setActiveDetail(null);
                  }}
                  className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                >
                  Close
                </button>
              </div>

              {detailLoading || !activeDetail ? (
                <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">Loading pack from API…</div>
              ) : (
                <div className="px-5 sm:px-6 py-5 space-y-5">
                  {panel.mode === 'view' ? (
                    <dl className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
                      {[
                        { label: 'Maker', value: activeDetail.maker },
                        { label: 'Model', value: activeDetail.model },
                        { label: 'Category', value: typeLabel(activeDetail.equipmentType) },
                        { label: 'Tasks', value: String(activeDetail.tasks?.length || 0) },
                      ].map((item) => (
                        <div key={item.label} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/25 px-4 py-3">
                          <dt className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))] font-semibold">{item.label}</dt>
                          <dd className="mt-1.5 text-sm font-semibold text-[hsl(var(--foreground))]">{item.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4 bg-[hsl(var(--muted))]/15 rounded-2xl border border-[hsl(var(--border))]/70">
                      <label className={labelClass}>
                        Maker
                        <input
                          className="mt-1 w-full px-3 py-2 border border-[hsl(var(--border))] rounded-xl text-sm font-normal bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                          value={editMeta[activeDetail.templateKey]?.maker ?? activeDetail.maker}
                          onChange={(e) =>
                            setEditMeta((prev) => ({
                              ...prev,
                              [activeDetail.templateKey]: {
                                maker: e.target.value,
                                model: prev[activeDetail.templateKey]?.model ?? activeDetail.model,
                                equipmentType: prev[activeDetail.templateKey]?.equipmentType ?? activeDetail.equipmentType,
                              },
                            }))
                          }
                        />
                      </label>
                      <label className={labelClass}>
                        Model
                        <input
                          className="mt-1 w-full px-3 py-2 border border-[hsl(var(--border))] rounded-xl text-sm font-normal bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                          value={editMeta[activeDetail.templateKey]?.model ?? activeDetail.model}
                          onChange={(e) =>
                            setEditMeta((prev) => ({
                              ...prev,
                              [activeDetail.templateKey]: {
                                model: e.target.value,
                                maker: prev[activeDetail.templateKey]?.maker ?? activeDetail.maker,
                                equipmentType: prev[activeDetail.templateKey]?.equipmentType ?? activeDetail.equipmentType,
                              },
                            }))
                          }
                        />
                      </label>
                      <label className={labelClass}>
                        Equipment type
                        <EquipmentTypeSelect
                          className="mt-1 w-full px-3 py-2 border border-[hsl(var(--border))] rounded-xl text-sm font-normal bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                          value={editMeta[activeDetail.templateKey]?.equipmentType ?? activeDetail.equipmentType}
                          onChange={(v) =>
                            setEditMeta((prev) => ({
                              ...prev,
                              [activeDetail.templateKey]: {
                                equipmentType: v,
                                maker: prev[activeDetail.templateKey]?.maker ?? activeDetail.maker,
                                model: prev[activeDetail.templateKey]?.model ?? activeDetail.model,
                              },
                            }))
                          }
                          catalog={equipmentCatalog}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => void saveTemplateMeta(activeDetail.templateKey)}
                        className="sm:col-span-2 xl:col-span-3 text-left text-xs font-semibold text-[#0088cc] hover:underline"
                      >
                        Save maker / model / type
                      </button>
                    </div>
                  )}

                  {activeDetail.description && (
                    <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{activeDetail.description}</p>
                  )}

                  {panel.mode === 'edit' && (
                    <div className="p-4 sm:p-5 border border-dashed border-[#0088cc]/35 rounded-2xl bg-[#0088cc]/5 dark:bg-[#0088cc]/10 space-y-3.5">
                      <h3 className="text-[11px] font-semibold text-[hsl(var(--foreground))] uppercase tracking-[0.1em]">Add maintenance task manually</h3>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <input
                          className="px-3 py-2.5 border border-[hsl(var(--border))] rounded-xl text-sm lg:col-span-2 bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                          placeholder="Task name * e.g. Change lube oil"
                          value={getTaskForm(activeDetail.templateKey).taskName}
                          onChange={(e) =>
                            setTaskForms((prev) => ({
                              ...prev,
                              [activeDetail.templateKey]: { ...getTaskForm(activeDetail.templateKey), taskName: e.target.value },
                            }))
                          }
                        />
                        <select
                          className="px-3 py-2.5 border border-[hsl(var(--border))] rounded-xl text-sm bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                          value={getTaskForm(activeDetail.templateKey).taskType}
                          onChange={(e) =>
                            setTaskForms((prev) => ({
                              ...prev,
                              [activeDetail.templateKey]: { ...getTaskForm(activeDetail.templateKey), taskType: e.target.value },
                            }))
                          }
                        >
                          {(equipmentCatalog?.taskTypes ?? [{ id: 'routine_maintenance', label: 'Routine maintenance' }]).map((tt) => (
                            <option key={tt.id} value={tt.id}>
                              {tt.label}
                            </option>
                          ))}
                        </select>
                        <select
                          className="px-3 py-2.5 border border-[hsl(var(--border))] rounded-xl text-sm bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                          value={getTaskForm(activeDetail.templateKey).frequencyType}
                          onChange={(e) =>
                            setTaskForms((prev) => ({
                              ...prev,
                              [activeDetail.templateKey]: { ...getTaskForm(activeDetail.templateKey), frequencyType: e.target.value },
                            }))
                          }
                        >
                          {(equipmentCatalog?.frequencyTypes ?? [{ id: 'running_hours', label: 'Running hours (RH)' }]).map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                        {getTaskForm(activeDetail.templateKey).frequencyType === 'running_hours' ? (
                          <input
                            type="number"
                            list={`rh-presets-${activeDetail.templateKey}`}
                            className="px-3 py-2.5 border border-[hsl(var(--border))] rounded-xl text-sm bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                            placeholder="RH interval e.g. 600"
                            value={getTaskForm(activeDetail.templateKey).runningHourInterval}
                            onChange={(e) =>
                              setTaskForms((prev) => ({
                                ...prev,
                                [activeDetail.templateKey]: {
                                  ...getTaskForm(activeDetail.templateKey),
                                  runningHourInterval: e.target.value,
                                },
                              }))
                            }
                          />
                        ) : (
                          <select
                            className="px-3 py-2.5 border border-[hsl(var(--border))] rounded-xl text-sm bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                            value={getTaskForm(activeDetail.templateKey).responsibleRank}
                            onChange={(e) =>
                              setTaskForms((prev) => ({
                                ...prev,
                                [activeDetail.templateKey]: {
                                  ...getTaskForm(activeDetail.templateKey),
                                  responsibleRank: e.target.value,
                                },
                              }))
                            }
                          >
                            {(equipmentCatalog?.responsibleRanks ?? ['2/E']).map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        )}
                        <datalist id={`rh-presets-${activeDetail.templateKey}`}>
                          {(equipmentCatalog?.rhIntervals ?? [600]).map((rh) => (
                            <option key={rh} value={rh} />
                          ))}
                        </datalist>
                        <select
                          className="px-3 py-2.5 border border-[hsl(var(--border))] rounded-xl text-sm bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                          value={getTaskForm(activeDetail.templateKey).criticality}
                          onChange={(e) =>
                            setTaskForms((prev) => ({
                              ...prev,
                              [activeDetail.templateKey]: { ...getTaskForm(activeDetail.templateKey), criticality: e.target.value },
                            }))
                          }
                        >
                          {CRITICALITY.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <input
                          className="px-3 py-2.5 border border-[hsl(var(--border))] rounded-xl text-sm lg:col-span-2 bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc]/25 outline-none"
                          placeholder="Description (optional)"
                          value={getTaskForm(activeDetail.templateKey).description}
                          onChange={(e) =>
                            setTaskForms((prev) => ({
                              ...prev,
                              [activeDetail.templateKey]: { ...getTaskForm(activeDetail.templateKey), description: e.target.value },
                            }))
                          }
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => void addTask(activeDetail.templateKey)}
                        className="inline-flex items-center gap-1.5 h-10 px-4 bg-[hsl(var(--foreground))] text-[hsl(var(--background))] rounded-xl text-xs font-semibold hover:opacity-90 transition-all duration-200 shadow-sm"
                      >
                        <MdAdd className="w-4 h-4" /> Add task
                      </button>
                    </div>
                  )}

                  <div className="overflow-x-auto rounded-2xl border border-[hsl(var(--border))]/70 bg-[hsl(var(--card))]/40">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]/80 bg-[hsl(var(--muted))]/25">
                          <th className="py-3 px-3 pr-2 font-semibold">Task</th>
                          <th className="py-3 px-3 pr-2 font-semibold">RH interval</th>
                          <th className="py-3 px-3 pr-2 font-semibold">Frequency</th>
                          <th className="py-3 px-3 pr-2 font-semibold">Rank</th>
                          {panel.mode === 'edit' && (
                            <th className="py-3 pl-3 pr-4 font-semibold text-right whitespace-nowrap min-w-[9.5rem]">
                              Edit RH / Delete
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {(activeDetail.tasks || []).map((task) => {
                          const editKey = `${activeDetail.templateKey}:${task.taskKey}`;
                          return (
                            <tr key={task.taskKey} className="border-t border-[hsl(var(--border))]/70 hover:bg-[hsl(var(--muted))]/25 transition-colors duration-150">
                              <td className="py-3 px-3 font-medium text-[hsl(var(--foreground))]">{task.taskName}</td>
                              <td className="py-3 px-3">
                                {task.runningHourInterval ? (
                                  <span className="inline-flex items-center gap-1 font-mono text-[hsl(var(--foreground))]">
                                    <MdSpeed className="w-3.5 h-3.5 text-[#0088cc]" />
                                    {task.runningHourInterval}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="py-3 px-3 text-xs text-[hsl(var(--muted-foreground))]">{task.frequencyType.replace(/_/g, ' ')}</td>
                              <td className="py-3 px-3 text-xs text-[hsl(var(--muted-foreground))]">{task.responsibleRank || '—'}</td>
                              {panel.mode === 'edit' && (
                                <td className="py-3 pl-3 pr-4 min-w-[9.5rem]">
                                  <div className="flex flex-row flex-wrap items-center justify-end gap-2 overflow-visible">
                                    {task.frequencyType === 'running_hours' && (
                                      <div className="flex gap-1.5 items-center shrink-0">
                                        <input
                                          type="number"
                                          aria-label="Edit running hours interval"
                                          placeholder={String(task.runningHourInterval || '')}
                                          value={editingRh[editKey] ?? ''}
                                          onChange={(e) =>
                                            setEditingRh((prev) => ({ ...prev, [editKey]: e.target.value }))
                                          }
                                          className="w-16 px-2 py-1.5 border border-[hsl(var(--border))] rounded-lg text-xs bg-[hsl(var(--background))]/80 text-[hsl(var(--foreground))]"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => void saveTaskRh(activeDetail.templateKey, task.taskKey)}
                                          className="text-[10px] font-semibold text-[#0088cc] hover:underline whitespace-nowrap"
                                        >
                                          RH
                                        </button>
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => void deleteTask(activeDetail.templateKey, task.taskKey)}
                                      className="inline-flex items-center justify-center shrink-0 p-1.5 rounded-lg bg-rose-500/10 text-rose-600 border border-rose-500/25 hover:bg-rose-500/20 dark:text-rose-400 dark:hover:text-rose-300 transition-colors"
                                      title="Remove task"
                                      aria-label="Remove task"
                                    >
                                      <MdDelete className="w-4 h-4 shrink-0" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                        {(activeDetail.tasks || []).length === 0 && (
                          <tr>
                            <td colSpan={panel.mode === 'edit' ? 5 : 4} className="py-8 text-center text-xs text-[hsl(var(--muted-foreground))]">
                              {panel.mode === 'edit'
                                ? 'No tasks yet — use the form above to add RH or calendar jobs'
                                : 'No tasks on this pack'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
